import { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { withoutBidiControls as plain } from "../snippets/bidi";
import { fillWhat, fillWhen, fillWhy } from "./lib/fills";
import { outcomeUnknown } from "./lib/outcome";
import { revealLater } from "./lib/reveal";
import useSubmitGuard from "../../hooks/useSubmitGuard";

const MAIN = "h-11 md:h-9";
const REASON_MAX = 1000; // InvestingFillAcknowledge.reason's maxLength

/**
 * Acknowledge one unauthorized fill (walkthrough 10, spec §2.1: a person's,
 * in their own browser): it says what the fill was and why it's flagged,
 * and what acknowledging does — it stops showing red; nothing trades or is
 * cancelled; it shows red again if it fills further. An optional reason is
 * sent only when given. A 409 (it isn't a flagged fill any more) is said
 * in the server's words, with a reload. No answer or a 5xx leaves the
 * outcome unknown: `onUnknown` closes it, and the banner says so and reads
 * the list again.
 *
 * The house dialog contract: static backdrop, Escape and Cancel refused
 * while it runs, a bounded body that scrolls, focus on the reason field.
 * `fill` is held from the moment it opened.
 */
function AcknowledgeFillDialog({ open, fill, onOpenChange, onAcknowledged, onReload, onUnknown, onCloseAutoFocus }) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null); // {message, conflict}
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef(null);
  const errorRef = useRef(null);
  const submitGuard = useSubmitGuard(open);

  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReason("");
      setError(null);
      setBusy(false);
    }
  }

  const acknowledge = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await investingApi.fills.acknowledge(fill.id, reason.trim());
      setBusy(false);
      onAcknowledged(fill);
    } catch (err) {
      setBusy(false);
      // No answer, or the server failed partway: it may have landed.
      if (outcomeUnknown(err)) {
        onUnknown({ fill, message: apiErrorMessage(err, "No answer came back.") });
        return;
      }
      const conflict = err?.response?.status === 409;
      const message = apiErrorMessage(err, "Couldn't acknowledge it.");
      setError({ message: conflict ? `Nothing changed: ${message}` : message, conflict });
      revealLater(errorRef);
    }
  };

  // After a 409 the page is stale, however the confirm closes: read it again.
  const close = () => (error?.conflict ? onReload() : onOpenChange(false));

  const why = fillWhy(fill);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : close())}>
      <DialogContent
        data-investing=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          reasonRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">Acknowledge this unauthorized order?</DialogTitle>
          <DialogDescription>
            It stops showing red, and stays recorded as unauthorized. Nothing trades or is cancelled — if it&apos;s
            still working at the broker, cancel it in Robinhood. If it fills further, it shows red again.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={acknowledge} noValidate className="flex min-h-0 flex-1 flex-col">
          <div
            data-slot="dialog-body"
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4"
          >
            <div className="rounded-md border border-destructive/60 bg-destructive/5 p-3 text-sm wrap-anywhere">
              <p className="m-0 font-semibold">{fillWhat(fill)}</p>
              <p className="m-0 text-muted-foreground">
                {fillWhen(fill)} · broker order{" "}
                <span className="inline-block max-w-full">{plain(fill.broker_order_id)}</span>
              </p>
              <p className="mt-2 mb-0">{why.reason}</p>
              {why.detail && <p className="mt-1 mb-0 text-muted-foreground">{why.detail}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-reason`}>Reason (optional)</Label>
              <Textarea
                ref={reasonRef}
                id={`${id}-reason`}
                value={reason}
                maxLength={REASON_MAX}
                placeholder="e.g. I placed it myself in the app"
                className="max-h-48 min-h-16"
                disabled={busy}
                aria-describedby={`${id}-reason-help`}
                onChange={(event) => setReason(event.target.value)}
              />
              <p id={`${id}-reason-help`} className="m-0 text-xs text-muted-foreground">
                What it was — kept with the acknowledgement.
              </p>
            </div>
            {error && (
              <Alert ref={errorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription className="flex flex-col items-start gap-2">
                  <span>{error.message}</span>
                  {error.conflict && (
                    <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={onReload}>
                      Reload the list
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" className={MAIN} disabled={busy} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" className={MAIN} disabled={busy} {...submitGuard}>
              {busy && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy ? "Acknowledging…" : "Acknowledge"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

AcknowledgeFillDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The unauthorized fill as it was shown when the confirm opened. */
  fill: PropTypes.shape({
    id: PropTypes.number.isRequired,
    broker_order_id: PropTypes.string.isRequired,
    symbol: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    unauthorized_reason: PropTypes.string,
    unauthorized_detail: PropTypes.string,
  }).isRequired,
  onOpenChange: PropTypes.func.isRequired,
  /** `(fill)`: acknowledged — the fill as it was shown. */
  onAcknowledged: PropTypes.func.isRequired,
  /** A 409's reload: close, and read the list again — on any close after a 409. */
  onReload: PropTypes.func.isRequired,
  /** `({fill, message})`: no answer or a 5xx — it may or may not have landed. */
  onUnknown: PropTypes.func.isRequired,
  /** Radix's close autofocus: the banner moves focus when the opener goes away. */
  onCloseAutoFocus: PropTypes.func,
};

export default AcknowledgeFillDialog;
